﻿import { useEffect, type ChangeEvent } from "react";
import { useState } from "react";
import { Typography, TextField, Grid, Autocomplete } from "@mui/material";
import { useSelector } from "react-redux";
import { useAppDispatch } from "@/typedDispatch";
import {
  fetchItemCodes,
  selectEditCatalogData,
  selectIsEditMode,
  selectAddEditConnectionInfo,
  setCatalogDataField,
  selectAvailableItems,
  type IItemArtifactData,
  type IAddCatalogPopup,
} from "@/features/connections/manageCatalogDataSlice";
import { defaultCode, blankCode } from "@/features/connections/integrationSlice";
import { AddEditCatalogPopup } from "./CatalogPopup";
import { CatalogueTypeNone } from "@/utils/utils";
import _ from "lodash";

const AddItemUOMPopup = ({ onClose, onValidate, onComplete }: IAddCatalogPopup) => {
  const dispatch = useAppDispatch();
  const dataValues = useSelector(selectEditCatalogData);
  const isEdit = useSelector(selectIsEditMode);
  const [selectedItem, setItem] = useState<IItemArtifactData>({
    ArtifactDictionaryDataId: dataValues.ItemId,
    Code: dataValues.ItemCode ? String(dataValues.ItemCode) : "None",
  });
  const [itemCodeSearchValue, setItemCodeSearchValue] = useState<string>("");
  const connectionInfo = useSelector(selectAddEditConnectionInfo);
  const itemCodes = useSelector(selectAvailableItems);
  
  const [options, setOptions] = useState<IItemArtifactData[]>([]);

  useEffect(() => {
    const list = Array.isArray(itemCodes) ? itemCodes : [];
    setOptions([{ Code: "None", Description: "" }, ...list]);
  }, [itemCodes])

  useEffect(() => {
    if (connectionInfo.connectionId !== -1) {
      dispatch(
        fetchItemCodes({ selectedConnectionId: connectionInfo.connectionId }),
      );
    }
  }, [connectionInfo, dispatch]);

  const fetchItemCodeWrapper = (
    selectedConnectionId: number,
    searchString: string = ""
  ) => {
    setOptions([]);
    dispatch(
      fetchItemCodes({
        selectedConnectionId,
        searchString
      }),
    );
  }
  const debouncedFetchItemCodeWrapper = _.debounce(fetchItemCodeWrapper, 500)

  useEffect(() => {
    if (
        itemCodeSearchValue 
        && itemCodeSearchValue.length >= 2
        && itemCodeSearchValue !== CatalogueTypeNone
        && connectionInfo.connectionId !== -1
      ) {
      debouncedFetchItemCodeWrapper(connectionInfo.connectionId, itemCodeSearchValue)
    } else if (
        !itemCodeSearchValue 
        || itemCodeSearchValue.length < 2
        || itemCodeSearchValue === CatalogueTypeNone
    ) {
      debouncedFetchItemCodeWrapper(connectionInfo.connectionId)
    }
  }, [connectionInfo, itemCodeSearchValue, debouncedFetchItemCodeWrapper, dispatch])

  if (selectedItem.ArtifactDictionaryDataId !== dataValues.ItemId) {
    setItem({
      ArtifactDictionaryDataId: dataValues.ItemId,
      Code: dataValues.ItemCode ? String(dataValues.ItemCode) : "None",
      Description: "",
    });
  }

  const handleItemChange = (newItem: IItemArtifactData | null) => {
    if (newItem && newItem.Code !== "None") {
      dispatch(
        setCatalogDataField({
          name: "ItemCode",
          value: newItem.Code,
        }),
      );
      dispatch(
        setCatalogDataField({
          name: "ItemId",
          value: newItem.ArtifactDictionaryDataId,
        }),
      );
    } else {
      dispatch(setCatalogDataField({ name: "ItemCode", value: null }));
      dispatch(setCatalogDataField({ name: "ItemId", value: null }));
    }
  };

  const validateForm = () => {
    if (onValidate) {
      const result = onValidate(dataValues);
      if (!result.isValid) return result;
    }
    //can no longer be in this state naturally, "None" is now the default option, not nothing selected
    if (selectedItem == null) {
      return {
        isValid: false,
        message: "Please select an option from the Item menu.",
      };
    }
    if (dataValues.UOM === defaultCode) {
      return {
        isValid: false,
        message: `UOM Code cannot be saved as ${defaultCode}`,
      };
    }
    if (dataValues.UOM === blankCode) {
      return {
        isValid: false,
        message: `UOM Code cannot be saved as ${blankCode}`,
      };
    }
    if (dataValues.UOM?.includes(",")) {
      return {
        isValid: false,
        message: `The UOM Code cannot include a ','`,
      };
    }
    if (
      dataValues.BaseFactor &&
      !Number.isFinite(Number(dataValues.BaseFactor))
    ) {
      return {
        isValid: false,
        message: "Item Base UOM Factor should be a valid number.",
      };
    }
    return { isValid: true };
  };

  const handleClose = () => {
    onClose();
    setItem({
      ArtifactDictionaryDataId: null,
      Code: "None",
    });
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    dispatch(setCatalogDataField({ name, value }));
  };

  return (
    <AddEditCatalogPopup
      onClose={handleClose}
      onValidate={validateForm}
      onComplete={onComplete}
    >
      <Grid item xs={4}>
        <Typography>
          Item Code
          <Typography
            component="span"
            sx={{ color: "red", fontSize: "1.2rem", fontWeight: "bold" }}
          >
            *
          </Typography>
        </Typography>
        <Autocomplete
          disabled={isEdit}
          options={options}
          getOptionLabel={(option) =>
            option.Code === "None"
              ? "None"
              : `${option.Code ?? ""} ${option.Description ?? ""}`
          }
          getOptionKey={(option) => option.Code}
          isOptionEqualToValue={(option, value) => option.Code === value.Code}
          value={selectedItem || null}
          onChange={(_e, newValue) => {
            handleItemChange(newValue || null);
          }}
          renderInput={(params) => (
            <TextField
              sx={{ marginTop: 1, marginBottom: 2, maxWidth: 250 }}
              {...params}
              autoComplete="off"
            />
          )}
          inputValue={itemCodeSearchValue}
          onInputChange={(_e, newValue) => {
            setItemCodeSearchValue(newValue)
          }}
        />      
      </Grid>
      <Grid item xs={4}>
        <Typography>UOM Code</Typography>
        <Typography>
          <TextField
            disabled={isEdit}
            variant="outlined"
            name="UOM"
            value={dataValues?.UOM || ""}
            onChange={handleChange}
            sx={{
              width: 250,
              marginTop: 1,
              marginBottom: 2,
            }}
          />
        </Typography>
      </Grid>
      <Grid item xs={4}>
        <Typography>Item Base UOM Factor</Typography>
        <Typography>
          <TextField
            variant="outlined"
            name="BaseFactor"
            value={dataValues?.BaseFactor || ""}
            onChange={handleChange}
            sx={{
              width: 250,
              marginTop: 1,
              marginBottom: 2,
            }}
          />
        </Typography>
      </Grid>
      <Grid item xs={4}>
        <Typography>Item Base UOM</Typography>
        <Typography>
          <TextField
            variant="outlined"
            name="BaseUOM"
            value={dataValues?.BaseUOM || ""}
            onChange={handleChange}
            sx={{
              width: 250,
              marginTop: 1,
              marginBottom: 2,
            }}
          />
        </Typography>
      </Grid>
      <Grid item xs={4} />
      <Grid item xs={4} />
    </AddEditCatalogPopup>
  );
};

export default AddItemUOMPopup;
